/**
 * PKCE helper for connecting a provider account.
 *
 * PKCE exists so a browser app can complete an authorisation without holding a
 * client secret: we generate a random verifier, send only its SHA-256 hash to
 * the provider, and prove ownership later by presenting the original. The
 * verifier lives in sessionStorage for the duration of the round trip and is
 * deleted as soon as it is used.
 */

const VERIFIER_KEY = "numa_oauth_verifier";
const PROVIDER_KEY = "numa_oauth_provider";

function base64url(bytes) {
  return btoa(String.fromCharCode(...new Uint8Array(bytes)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function randomVerifier() {
  // 32 bytes -> 43 base64url characters, the length the spec recommends.
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64url(bytes);
}

async function challengeFor(verifier) {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64url(digest);
}

/** Returns the challenge to send, having stashed the verifier for the return leg. */
export async function beginAuthorisation(provider) {
  const verifier = randomVerifier();
  try {
    sessionStorage.setItem(VERIFIER_KEY, verifier);
    sessionStorage.setItem(PROVIDER_KEY, provider);
  } catch {
    // Private browsing with storage blocked: the round trip cannot complete,
    // so say so now rather than after the user has authorised on the provider.
    throw new Error(
      "Il browser sta bloccando la memoria di sessione: non è possibile completare l'accesso. Usa una API key."
    );
  }
  return { verifier, challenge: await challengeFor(verifier) };
}

export function pendingAuthorisation() {
  try {
    const verifier = sessionStorage.getItem(VERIFIER_KEY);
    const provider = sessionStorage.getItem(PROVIDER_KEY);
    return verifier && provider ? { verifier, provider } : null;
  } catch {
    return null;
  }
}

export function clearAuthorisation() {
  try {
    sessionStorage.removeItem(VERIFIER_KEY);
    sessionStorage.removeItem(PROVIDER_KEY);
  } catch {
    /* ignore */
  }
}

export function callbackUrl(path) {
  return `${window.location.origin}${path}`;
}
